import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { usePWA } from '@/hooks/usePWA'
import { MainLayout } from '@/components/layouts/main-layout'
import { AuthLayout } from '@/components/layouts/auth-layout'
import { LoadingPage } from '@/components/ui/spinner'
import { GlobalAlertDialog } from '@/components/global-alert-dialog'

// Pages
import { DashboardPage } from '@/pages/dashboard'
import { LoginPage } from '@/pages/auth/login'
import { RegisterPage } from '@/pages/auth/register'
import { AuthCallbackPage } from '@/pages/auth/callback'
import { ContribuicaoPage } from '@/pages/contribuicao'
import { EventosPage } from '@/pages/eventos'
import { EventosNovoPage } from '@/pages/eventos-novo'
import { EventosEditarPage } from '@/pages/eventos-editar'
import { EventoInscricaoPage } from '@/pages/evento-inscricao'
import { EventoDetalhePage } from '@/pages/evento-detalhe/index'
import { EventoParticipanteNovoPage } from '@/pages/evento-participante-novo'
import { EventoCarnePage } from '@/pages/evento-carne'
import { MinisteriosPage } from '@/pages/ministerios'
import { MinisterioDetalhePage } from '@/pages/ministerio-detalhe'
import { MembrosPage } from '@/pages/membros'
import { MembrosNovoPage } from '@/pages/membros-novo'
import { MembrosDetalhePage } from '@/pages/membros-detalhe'
import { BibliaPage } from '@/pages/biblia'
import { SermoesPage } from '@/pages/sermoes'
import { SermoesNovoPage } from '@/pages/sermoes-novo'
import { FinancasPage } from '@/pages/financas'
import { FinancasPainel } from '@/pages/financas/painel'
import { FinancasAdministracao } from '@/pages/financas/administracao'
import { EntradasPage } from '@/pages/financas/entradas'
import { DespesasOperacionaisPage } from '@/pages/financas/despesas-operacionais'
import { ManutencaoReformaPage } from '@/pages/financas/manutencao-reforma'
import { SalariosPage } from '@/pages/financas/salarios'
import { SalariosNovoPage } from '@/pages/financas/salarios-novo'
import { InvestimentosPage } from '@/pages/financas/investimentos'
import { ImpostosPage } from '@/pages/financas/impostos'
import { MissoesPage } from '@/pages/financas/missoes'

// Páginas de finanças que já existem
// Removendo imports de componentes que não existem
import { CadastrarEntradaPage } from '@/pages/financas/entradas/cadastrar'
import { CadastrarDespesaOperacionalPage } from '@/pages/financas/despesas-operacionais/cadastrar'
import { CadastrarMissaoPage } from '@/pages/financas/missoes/cadastrar'
import { CadastrarInvestimentoPage } from '@/pages/financas/investimentos/cadastrar'
import { CadastrarProjetoSocialPage } from '@/pages/financas/projetos-sociais/cadastrar'
import { CadastrarImpostoPage } from '@/pages/financas/impostos/cadastrar'
import { SolicitacoesMinistriaisPage } from './pages/financas/solicitacoes-ministeriais'
import SolicitacaoDetalhePage from './pages/financas/solicitacao-detalhe'
import EnviarSaldoPage from '@/pages/financas/enviar-saldo'
import { ConciliacaoBancariaPage } from '@/pages/financas/conciliacao-bancaria'
import { ProjetosSociaisPage } from '@/pages/financas/projetos-sociais'
import { AdminAppPage } from '@/pages/admin-app'
import { MinhaIgrejaPage } from '@/pages/minha-igreja'
import PerfilPage from '@/pages/perfil'
import { EditarIgrejaPage } from '@/pages/editar-igreja'
import { UsuarioEditarPage } from '@/pages/usuario-editar'
import { IgrejaNovaPage } from '@/pages/igreja-nova'
import { UsuarioNovoPage } from '@/pages/usuario-novo'
import { MenuMobilePage } from '@/pages/menu-mobile'

const App = React.memo(() => {
  // Inicializa o PWA dinâmico
  usePWA()

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>

        {/* Auth callback - Public route (OAuth redirect) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Public event registration */}
        <Route path="/eventos/:eventoSlug/inscricao" element={<EventoInscricaoPage />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          {/* Mobile menu - standalone route without MainLayout */}
          <Route path="/menu-mobile" element={<MenuMobilePage />} />
          
          <Route element={<MainLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/contribuicao" element={<ContribuicaoPage />} />
            <Route path="/eventos" element={<EventosPage />} />
            <Route path="/eventos/novo" element={<EventosNovoPage />} />
            <Route path="/eventos/:eventoSlug" element={<EventoDetalhePage />} />
            <Route path="/eventos/:eventoSlug/editar" element={<EventosEditarPage />} />
            <Route
              path="/eventos/:eventoSlug/participantes/novo"
              element={<EventoParticipanteNovoPage />}
            />
            <Route
              path="/eventos/:eventoSlug/participantes/:participanteId/carne"
              element={<EventoCarnePage />}
            />
            <Route path="/ministerios" element={<MinisteriosPage />} />
            <Route path="/ministerios/:id" element={<MinisterioDetalhePage />} />
            <Route path="/membros" element={<MembrosPage />} />
            <Route path="/membros/novo" element={<MembrosNovoPage />} />
            <Route path="/membros/:id" element={<MembrosDetalhePage />} />
            <Route path="/biblia" element={<BibliaPage />} />
            <Route path="/sermoes" element={<SermoesPage />} />
            <Route path="/sermoes/novo" element={<SermoesNovoPage />} />
            <Route path="/financas" element={<FinancasPage />}>
              <Route index element={<Navigate to="/financas/painel" replace />} />
              <Route path="painel" element={<FinancasPainel />} />
              <Route path="administracao" element={<FinancasAdministracao />} />
              
              {/* Rotas aninhadas para Entradas */}
              <Route path="administracao/entradas" element={<EntradasPage />} />
              <Route path="administracao/entradas/cadastrar" element={<CadastrarEntradaPage />} />
              <Route path="administracao/entradas/conciliacao-bancaria" element={<ConciliacaoBancariaPage />} />
              
              {/* Rotas aninhadas para Despesas Operacionais */}
              <Route path="administracao/despesas-operacionais" element={<DespesasOperacionaisPage />} />
              <Route path="administracao/despesas-operacionais/cadastrar" element={<CadastrarDespesaOperacionalPage />} />
              
              {/* Rotas aninhadas para Missões */}
              <Route path="administracao/missoes" element={<MissoesPage />} />
              <Route path="administracao/missoes/cadastrar" element={<CadastrarMissaoPage />} />
              
              {/* Rotas aninhadas para Investimentos */}
              <Route path="administracao/investimentos" element={<InvestimentosPage />} />
              <Route path="administracao/investimentos/cadastrar" element={<CadastrarInvestimentoPage />} />
              
              {/* Rotas aninhadas para Projetos Sociais */}
              <Route path="administracao/projetos-sociais" element={<ProjetosSociaisPage />} />
              <Route path="administracao/projetos-sociais/cadastrar" element={<CadastrarProjetoSocialPage />} />
              
              {/* Rotas aninhadas para Impostos */}
              <Route path="administracao/impostos" element={<ImpostosPage />} />
              <Route path="administracao/impostos/cadastrar" element={<CadastrarImpostoPage />} />
              
              {/* Rotas que mantêm a estrutura original */}
              <Route path="administracao/manutencao-reforma" element={<ManutencaoReformaPage />} />
              <Route path="administracao/salarios" element={<SalariosPage />} />
              <Route path="administracao/solicitacoes-ministeriais" element={<SolicitacoesMinistriaisPage />} />
            </Route>
            <Route path="/financas/salarios/novo" element={<SalariosNovoPage />} />
            <Route path="/financas/solicitacoes-ministeriais/:id" element={<SolicitacaoDetalhePage />} />
            <Route path="/financas/enviar-saldo/:id" element={<EnviarSaldoPage />} />
            <Route path="/admin-app" element={<AdminAppPage />} />
            <Route path="/igreja-nova" element={<IgrejaNovaPage />} />
            <Route path="/usuario-novo" element={<UsuarioNovoPage />} />
            <Route path="/igreja/:id/editar" element={<EditarIgrejaPage />} />
            <Route path="/usuario/:id/editar" element={<UsuarioEditarPage />} />
            <Route path="/minha-igreja" element={<MinhaIgrejaPage />} />
            <Route path="/perfil" element={<PerfilPage />} />
          </Route>
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <GlobalAlertDialog />
    </BrowserRouter>
  )
})

App.displayName = 'App'

const ProtectedRoute = React.memo(() => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingPage message="Verificando autenticação..." variant="bounce" size="lg" />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <Outlet />
})

ProtectedRoute.displayName = 'ProtectedRoute'

export default App
